import {
	PET_INSURANCE_FAQS,
	PET_INSURANCE_HEADER_IMAGE,
	PET_INSURANCE_PROVIDERS,
} from "@/data/consts";
import * as React from "react";
import Image from "next/image";
import FAQs from "@/components/FAQs";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { getOne } from "@/api/contentful";
import { Providers } from "@/types/providers";
import { createDealClick } from "@/api/tracking";
import PageSEO from "@/components/Shared/PageSEO";
import { UtmContext } from "@/context/utmContext";
import { KeywordContext } from "@/context/keyword";
import { FAQS } from "@/types/index";
import { HeaderImage as HeaderImageType } from "@/types/header";
import InsuranceCards from "@/components/Insurance/InsuranceCardList";
import InsuranceBenefits from "@/components/Insurance/InsuranceBenefits";
import WeWorkWithInsurance from "@/components/Insurance/WeWorkWithInsurance";
import InsuranceHeaderContent from "@/components/Header/InsuranceHeaderContent";

const Breadcrumbs = [
	{
		title: "Home",
		href: "/",
		active: false,
	},
	{
		title: "Insurance",
		href: "/insurance",
		active: false,
	},
	{
		title: "Pet Insurance",
		href: "/pet-insurance",
		active: true,
	},
];

const headerContentListItems = [
	"Compare quotes with 15+ UK pet insurance providers",
	"You could pay from £3.35 per month*",
	"See if you can find a better pet insurance deal",
];

const insuranceBenefitsListItems = [
	{
		heading: "Time",
		text: "Quickly and easily compare insurance in one place",
	},
	{
		heading: "Variety",
		text: "Get access to over 15 insurance providers.",
	},

	{
		heading: "Free",
		text: "The comparison site is completely free to use.",
	},
];

const insuranceHref =
	"https://insurance.savely.co.uk/pet/index.php?id=64394a5915073aeda43a2395977b1405";

const PetInsurance: React.FC<{
	headerImage: HeaderImageType;
	FAQsContent: FAQS;
	providers: {
		fields: {
			providerLogos: Array<Providers>;
		};
	};
}> = ({ headerImage, FAQsContent, providers }) => {
	const { utmContent, utmCampaign, utmMedium, utmSource, utmTerm } =
		React.useContext(UtmContext);
	const { sclid, gclid, fbclid, msclkid, tbclid, obclid } =
		React.useContext(KeywordContext);

	const onDealClick = async () => {
		createDealClick({
			type: "pet-insurance",
			click_reference:
				sclid ?? gclid ?? fbclid ?? msclkid ?? tbclid ?? obclid ?? utmContent,
			utm_campaign: utmCampaign,
			utm_content: utmContent,
			utm_source: utmSource,
			utm_medium: utmMedium,
			utm_term: utmTerm,
			product: "Pet Insurance",
			provider: "N/A",
			metadata: {},
		});
	};

	function generateInsuranceHref(): string {
		let href = insuranceHref;
		if (sclid || gclid || fbclid || msclkid || tbclid || obclid || utmContent) {
			href +=
				"&kw=" +
				(sclid || gclid || fbclid || msclkid || tbclid || obclid || utmContent);
		}
		return href;
	}

	return (
		<div className="h-100 d-flex flex-column">
			<PageSEO
				title={
					"Compare Pet Insurance, Find the Best Coverage for Your Furry Friend | Consumer Choices"
				}
				description={
					"Compare pet insurance policies from top providers and find the best coverage for your furry friend. Protect your pet's health and save money with Consumer Choices comparison website. Find the right policy for your pet's needs today."
				}
				keywords={"Pet Insurance, Pet Insurance Comparison"}
			/>
			<Header
				left={
					<InsuranceHeaderContent
						title={"Compare Pet Insurance Quotes"}
						listItems={headerContentListItems}
						insuranceLink={generateInsuranceHref()}
						onClick={onDealClick}
					/>
				}
				right={
					<Image
						src={"https:" + headerImage.fields.image?.fields.file?.url}
						alt={headerImage.fields.image?.fields.title ?? ""}
						height="400"
						width="380"
					/>
				}
				theme={{
					paddingTop: 5,
					paddingRight: 0,
					paddingLeft: 0,
					paddingBottom: 0,
				}}
				breadcrumbs={Breadcrumbs}
			/>

			<InsuranceBenefits
				headingTitle={"Looking for pet insurance?"}
				benefits={insuranceBenefitsListItems}
			/>
			<WeWorkWithInsurance providers={providers.fields.providerLogos} />
			<InsuranceCards title={"Related Insurance"} />
			<FAQs data={FAQsContent} />
			<section className={` pt-5 pb-5 text-center bg-blue`}>
				<div className="container-fluid pt-5 pb-5">
					<InsuranceHeaderContent
						title={"Compare Pet Insurance Quotes"}
						listItems={headerContentListItems}
						insuranceLink={
							insuranceHref +
							(sclid ||
							gclid ||
							fbclid ||
							msclkid ||
							tbclid ||
							obclid ||
							utmContent
								? "&kw=" +
								  (sclid ||
										gclid ||
										fbclid ||
										msclkid ||
										tbclid ||
										obclid ||
										utmContent)
								: "")
						}
						bottomHeader={true}
						disclaimerText="*Price per month for cover based on a dog, Penelope, aged 4 months, no known medical
conditions, up-to-date vaccinations, and microchipped. Based on quote data provided by
Seopa Ltd during August 2024. The quote price you could achieve is dependent on your
individual circumstances."
						onClick={onDealClick}
					/>
				</div>
			</section>
			<Footer />
		</div>
	);
};

export async function getStaticProps() {
	const FAQsContent = await getOne(PET_INSURANCE_FAQS);
	const providers = await getOne(PET_INSURANCE_PROVIDERS);
	const headerImage = await getOne(PET_INSURANCE_HEADER_IMAGE);

	return {
		props: {
			headerImage,
			FAQsContent,
			providers,
		},
		revalidate: Number(process.env.REVALIDATE_CONTENTFUL),
	};
}

export default PetInsurance;
